import { useEffect, useMemo, useState } from "react";
import { createClinicalStatsApi } from "../api-helpers/ClinicalStatsApiHelper";
import { ClinicalStats } from "../api-helpers/ClinicalStatsTypes";

export type ClinicalStatsHookStatus = "loading" | "loaded" | "error";

interface UseClinicalStatsResult {
  status: ClinicalStatsHookStatus;
  stats: ClinicalStats | null;
  errorMessage: string | null;
}

// Fetch-on-mount only - no generate/regenerate, no Bedrock, no persisted
// request row. RespiratoryStatusBand already renders nothing when every
// field on ClinicalStats is empty, so there's no separate "empty" status
// here - "loaded" covers both a populated and an all-empty result.
export function useClinicalStats(
  patientId: string | undefined
): UseClinicalStatsResult {
  const api = useMemo(() => createClinicalStatsApi(), []);
  const [status, setStatus] = useState<ClinicalStatsHookStatus>("loading");
  const [stats, setStats] = useState<ClinicalStats | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!patientId) return;

    let cancelled = false;

    const fetchStats = async () => {
      setStatus("loading");
      setErrorMessage(null);
      try {
        const data = await api.getClinicalStats(patientId);
        if (cancelled) return;
        setStats(data);
        setStatus("loaded");
      } catch {
        if (!cancelled) {
          setStatus("error");
          setErrorMessage("Unable to load clinical stats.");
        }
      }
    };

    fetchStats();

    return () => {
      cancelled = true;
    };
  }, [api, patientId]);

  return { status, stats, errorMessage };
}
